import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRight } from 'lucide-react';
import type { UpdaterServer } from '../../types/modpackUpdater';

/** Inventory is evidence for an administrator, never an automatic assignment. */
export function InstanceMappingNotice({ server }: { server: UpdaterServer }) {
  const instances = server.agent?.instances;
  return <div className="mt-4 rounded-lg border border-amber-400/25 bg-amber-500/5 p-3 space-y-3">
    <div className="flex items-start gap-2">
      <AlertCircle className="w-4 h-4 shrink-0 text-amber-300 mt-0.5" />
      <div className="min-w-0 space-y-2 text-sm text-[var(--text-secondary)]">
        <p>The machine is online, but this server's configured AMP instance does not match its reported inventory.</p>
        <p>Expected AMP instance: <strong className="font-mono text-[var(--text-primary)] break-all">{server.amp_name || 'Not configured'}</strong></p>
        <Link to={`/modpack-configs/${encodeURIComponent(server.server_address)}?section=overview`} className="inline-flex items-start gap-1.5 text-accent-300 underline underline-offset-2">
          <span>Review AMP instance mapping in server settings</span><ArrowRight className="w-4 h-4 shrink-0 mt-0.5" />
        </Link>
        <p className="text-xs text-[var(--text-muted)]">Check the instance name and UUID before changing the AMP instance field. Editing that field requires the existing configuration admin permission.</p>
      </div>
    </div>
    <details className="rounded-lg border border-glass-200/10">
      <summary className="p-3 text-sm text-[var(--text-secondary)] cursor-pointer">Local AMP instances reported by this machine{instances ? ` (${instances.length})` : ''}</summary>
      {instances?.length ? <ul className="max-h-72 overflow-auto px-3 pb-3 divide-y divide-glass-200/10" aria-label="Reported local AMP instances">
        {instances.map(instance => <li key={instance.instance_id} className="py-2 space-y-1">
          <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-sm text-[var(--text-primary)] break-all">{instance.amp_name}</span>
            <span className="badge badge-neutral">{instance.configured ? 'Linked to a server config' : 'No server config mapping'}</span></div>
          <p className="font-mono text-xs text-[var(--text-muted)] break-all">{instance.instance_id}</p>
        </li>)}
      </ul> : <p className="px-3 pb-3 text-xs text-[var(--text-muted)]">{instances
        ? 'The agent reports no local AMP instances on this machine.'
        : 'Local instance names were not included in this report. Refresh after the next agent inventory update.'}</p>}
    </details>
  </div>;
}
